import { useMemo } from 'react';

import { Box, Flex } from '@chakra-ui/react';
import { staticRoutesWithCustomSidebar } from 'Deer/constants/staticRoutesWithCustomSidebar';
import { useDispatch, useSelector } from 'react-redux';
import { REVIEW_FORM_STAGE } from 'reducers/reviewCreationSlice';
import {
  setReviewCreationCalibrationAndDeliverables,
  setReviewCreationKey,
  useReviewCreationV1Data,
  useSelectReviewCreationCalibrationAndDeliverables,
} from 'reducers/reviewCreationV1Slice';
import { selectLoggedInEmployeeId } from 'selectors/loggedInEmployee';
import { checkEnableReviewWithoutSelfReview } from 'utils/helpers/featureFlags';

import { SettingsV1, Visibility } from 'assets/assetsV1/iconComponents';

import { REVIEW_PROVIDER_TYPES } from 'constants/ProjectConstants';

import { Button } from 'uiToolkitV1/Button';
import { SelectDashed } from 'uiToolkitV1/Select';
import { TooltipInfoV1 } from 'uiToolkitV1/TooltipInfoV1';
import { TextBodyMedium } from 'uiToolkitV1/Typography';

import { LabelFieldWrapper } from '../components/LabelFieldWrapper';
import { SectionWrapper } from '../components/SectionWrapper';
import { usePublishCalibrationSettings } from '../dataHooks/calibrationAndDeliverablesSectionHooks';
import { FinalScoreOutcomeGrid } from './calibrationAndDeliverables/FinalScoreOutcomeGrid';
import { VisibilityCards } from './calibrationAndDeliverables/VisibilityCards';

const RESULT_PUBLISHING_OPTIONS = [
  {
    label: 'Admin publishes to manager or employee',
    value: false,
  },
  {
    label: 'Manager or admin publishes to employee',
    value: true,
  },
];

export const PreviewResultButton = () => {
  const { feedbackFormId } = useReviewCreationV1Data();
  const loggedInEmployeeId = useSelector(selectLoggedInEmployeeId);

  const route = staticRoutesWithCustomSidebar.reviewsResultPage
    .replace(':reviewId', feedbackFormId)
    .replace(':employeeId', loggedInEmployeeId);
  const providerType = checkEnableReviewWithoutSelfReview()
    ? REVIEW_PROVIDER_TYPES.MANAGER
    : REVIEW_PROVIDER_TYPES.SELF;
  const routeWithQuery = `${route}?isPreview=true&providerType=${providerType}`;

  return (
    <Button target="_blank" as="a" variant="outlined" StartIcon={Visibility} height="32px" href={routeWithQuery}>
      Preview result
    </Button>
  );
};

const CalibrationAndDeliverables = () => {
  const dispatch = useDispatch();
  const { reviewStage } = useReviewCreationV1Data();
  const isDraft = reviewStage === REVIEW_FORM_STAGE.DRAFT;

  const { isManagerPublishAllowed } = useSelectReviewCreationCalibrationAndDeliverables();
  const { mutate: updatePublishSettings } = usePublishCalibrationSettings();

  const selectedPublishingOption = useMemo(
    () => RESULT_PUBLISHING_OPTIONS.find((option) => option.value === isManagerPublishAllowed),
    [isManagerPublishAllowed]
  );

  const handlePanelOpen = () => {
    dispatch(setReviewCreationKey({ isCalibrationAndDeliverablesPanelOpen: true }));
  };

  const onChangePublishingPermission = ({ value }: { value: boolean }) => {
    dispatch(
      setReviewCreationCalibrationAndDeliverables({
        isManagerPublishAllowed: value,
      })
    );
    updatePublishSettings({
      isManagerPublishAllowed: value,
    });
  };

  const rightComp = (
    <Flex gap={3}>
      <PreviewResultButton />

      <Button variant="outlined" StartIcon={SettingsV1} height="32px" onClick={handlePanelOpen}>
        Configure
      </Button>
    </Flex>
  );

  const labelComp = (
    <Flex alignItems="center" justifyContent="center">
      <TextBodyMedium color="text.g2" mr={1}>
        Result publishing permission:
      </TextBodyMedium>
      <TooltipInfoV1
        title={
          <>
            <Box>
              In both cases, the admin can publish the review directly to the employee. If the manager needs to publish
              the review:
            </Box>
            <Box>With option 1, the admin must first publish to the manager.</Box>
            <Box>With option 2, the manager candirectly publish the review post manager submission.</Box>
          </>
        }
      />
    </Flex>
  );

  return (
    <SectionWrapper
      title="Calibration & deliverables"
      subTitle="Final review scores & outcomes, result publishing & visibility permissions"
      rightComp={rightComp}
      restrictAccess={!isDraft}
    >
      <LabelFieldWrapper label="Final scores & outcomes">
        <Box gridColumn="2 / span 2">
          <FinalScoreOutcomeGrid onChangeClick={handlePanelOpen} />
        </Box>
      </LabelFieldWrapper>

      <LabelFieldWrapper labelComp={labelComp}>
        <SelectDashed
          value={selectedPublishingOption}
          options={RESULT_PUBLISHING_OPTIONS}
          handleChange={onChangePublishingPermission}
          width="100%"
          // below are TS errors
          placeholder={undefined}
          getOptionValue={undefined}
          getOptionLabel={undefined}
          selectRef={undefined}
        />
      </LabelFieldWrapper>

      <LabelFieldWrapper label="Visibility of shared results">
        <VisibilityCards />
      </LabelFieldWrapper>
    </SectionWrapper>
  );
};

export { CalibrationAndDeliverables };
